import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, ChevronDown, ChevronUp } from "lucide-react";
import { http, formatSoles, statusLabel } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PageHeader, Pill, ORDER_STATUS_COLORS, quoteStatus, fmtDate, exportXlsx } from "./ui";

function CustomerRow({ c, onSaved }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(c.notes || "");
  const [email, setEmail] = useState(c.email || "");
  async function save() {
    await http.put(`/customers/${encodeURIComponent(c.key)}`, { notes, email, name: c.name });
    toast.success("Cliente actualizado");
    onSaved();
  }
  return (
    <div className="bg-white rounded-xl border border-black/5" data-testid={`customer-${c.key}`}>
      <button className="w-full grid grid-cols-2 sm:grid-cols-6 gap-2 items-center p-4 text-left" onClick={() => setOpen(!open)} data-testid={`toggle-customer-${c.key}`}>
        <div className="sm:col-span-2 min-w-0"><div className="font-semibold truncate">{c.name || "(sin nombre)"}</div><div className="text-xs text-neutral-500">{c.phone}</div></div>
        <div className="text-xs text-neutral-500 truncate hidden sm:block">{c.email || "—"}</div>
        <div className="text-sm"><b>{c.orders.length}</b> pedidos · <b>{c.quotes.length}</b> cotiz.</div>
        <div className="text-sm"><div className="font-semibold">{formatSoles(c.total_spent)}</div><div className="text-[11px] text-neutral-500">Última compra: {fmtDate(c.last_purchase)}</div></div>
        <div className="flex justify-end">{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</div>
      </button>
      {open && (
        <div className="border-t p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">Pedidos</div>
            {c.orders.length === 0 && <p className="text-sm text-neutral-500">Sin pedidos</p>}
            {c.orders.slice().reverse().map((o) => (
              <Link key={o.id} to={`/admin/pedidos/${o.id}`} className="flex justify-between items-center py-1.5 text-sm hover:text-[var(--brand-primary)]">
                <span>{o.order_code} · {fmtDate(o.created_at)}</span>
                <span className="flex items-center gap-2">{formatSoles(o.total)} <Pill className={ORDER_STATUS_COLORS[o.status]}>{statusLabel(o.status)}</Pill></span>
              </Link>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">Cotizaciones</div>
            {c.quotes.length === 0 && <p className="text-sm text-neutral-500">Sin cotizaciones</p>}
            {c.quotes.slice().reverse().map((q) => (
              <Link key={q.id} to={`/admin/cotizaciones/${q.id}`} className="flex justify-between items-center py-1.5 text-sm hover:text-[var(--brand-primary)]">
                <span>{q.quote_code || "COT"} · {fmtDate(q.created_at)}</span>
                <Pill className={quoteStatus(q.status).cls}>{quoteStatus(q.status).label}</Pill>
              </Link>
            ))}
          </div>
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">Email</div>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">Observaciones</div>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`customer-notes-${c.key}`} />
            <Button size="sm" className="btn-primary" onClick={save} data-testid={`save-customer-${c.key}`}>Guardar</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomersPage() {
  const [cs, setCs] = useState([]);
  const [q, setQ] = useState("");
  const load = () => http.get("/customers").then((r) => setCs(r.data));
  useEffect(() => { load(); }, []);
  const list = useMemo(() => cs.filter((c) => `${c.name} ${c.phone} ${c.email}`.toLowerCase().includes(q.toLowerCase())), [cs, q]);
  const exportRows = () => exportXlsx(list.map((c) => ({
    Nombre: c.name, Teléfono: c.phone, Email: c.email, Pedidos: c.orders.length, Cotizaciones: c.quotes.length,
    "Total comprado": c.total_spent, "Última compra": fmtDate(c.last_purchase), Observaciones: c.notes,
  })), "clientes-floral-rosse", "Clientes");
  return (
    <div data-testid="admin-customers-page">
      <PageHeader title="Clientes" subtitle="Se agrupan automáticamente por número de teléfono"
        actions={<Button variant="outline" onClick={exportRows} data-testid="export-customers"><Download size={14} className="mr-1" /> Excel</Button>} />
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, teléfono o email" className="pl-9 bg-white" data-testid="customer-search" />
      </div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-neutral-500 py-10 text-center">Aún no hay clientes.</p>}
        {list.map((c) => <CustomerRow key={c.key} c={c} onSaved={load} />)}
      </div>
    </div>
  );
}
